/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Snackbar,
    Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Sidebar from '../../Components/Sidebar/Sidebar';
import Navbar from '../../Components/Navbar/Navbar';
import DynamicLoader from '../../Images/DynamicLoader';
import { categoryAPI, fileAPI } from '../../services/apiService';
import './categories.css';
import book1 from '../../Images/book1.jpg';

function Categories() {
    const { t, i18n } = useTranslation();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [editingIcon, setEditingIcon] = useState('');

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (location.state?.successMessage) {
            setSnackbar({
                open: true,
                message: location.state.successMessage,
                severity: 'success',
            });
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    useEffect(() => {
        const fetchCategories = async () => {
            if (!localStorage.getItem('token') || !localStorage.getItem('userGroup')) {
                setError(t('auth_or_group_missing'));
                setLoading(false);
                return;
            }

            try {
                const result = await categoryAPI.getAll({ fallback: [] });
                setCategories(result || []);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchCategories();
    }, [t]);

    const confirmDelete = (id) => {
        setSelectedId(id);
        setOpenDialog(true);
    };

    const handleCloseDelete = () => {
        setOpenDialog(false);
        setSelectedId(null);
    };

    const handleConfirmDelete = async () => {
        if (!selectedId) return;
        try {
            await categoryAPI.remove(selectedId);
            setCategories((prev) => prev.filter((cat) => cat.id !== selectedId));

            setSnackbar({
                open: true,
                message: t('delete_category_success'),
                severity: 'success',
            });
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: t('delete_category_error'),
                severity: 'error',
            });
        } finally {
            handleCloseDelete();
        }
    };

    const handleEdit = (cat) => {
        setEditingCategory({ ...cat });
        setOpenEditDialog(true);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditingCategory((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSaveEdit = async () => {
        if (!editingCategory) return;
        try {
            const { id } = editingCategory;
            const formData = new FormData();
            formData.append('title', editingCategory.title || '');
            formData.append(
                'priority',
                editingCategory.priority !== undefined ? String(editingCategory.priority) : ''
            );
            if (editingIcon) {
                const selectedIcon = DynamicLoader.find((icon) => icon.key === editingIcon);

                if (selectedIcon) {
                    const iconFile = await fileAPI.getSvgFileFromUrl(
                        selectedIcon.url,
                        `${editingIcon}.svg`
                    );

                    formData.append('image', iconFile);
                }
            }

            const updatedCat = await categoryAPI.update(id, formData);

            setCategories((prev) =>
                prev.map((c) =>
                    c.id === id
                        ? { ...c, ...editingCategory, image: updatedCat.image || c.image }
                        : c
                )
            );

            setSnackbar({
                open: true,
                message: t('changes_saved_success'),
                severity: 'success',
            });

            setOpenEditDialog(false);
            setEditingCategory(null);
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: t('edit_category_error'),
                severity: 'error',
            });
        }
    };

    const handleCloseSnackbar = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    return (
        <div className="categories_page">
            <Sidebar />
            <div className="categories_main">
                <Navbar />
                <div className="categoties_content">
                    <div className="categories_header">
                        <Button
                            className="add_new_category"
                            type="button"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/categories/addnew')}
                        >
                            {t('add_new_category')}
                        </Button>
                    </div>

                    {loading && <p>{t('loading')}</p>}
                    {error && (
                        <p>
                            {t('error')}:{error}
                        </p>
                    )}

                    {!loading && !error && (
                        <TableContainer component={Paper} className="categories_table">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center">{t('category_image')}</TableCell>
                                        <TableCell align="center">{t('category_name')}</TableCell>
                                        <TableCell align="center">{t('created_date')}</TableCell>
                                        <TableCell align="center">{t('priority')}</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {categories.map((cat) => (
                                        <TableRow key={cat.id}>
                                            <TableCell
                                                align="center"
                                                data-label={t('category_image')}
                                            >
                                                <div className="category_idd">
                                                    <img
                                                        src={cat.image || book1}
                                                        alt="category"
                                                        className="product_img"
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                data-label={t('category_name')}
                                            >
                                                {cat.title}
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                data-label={t('created_date')}
                                            >
                                                {cat.updated_date
                                                    ? new Date(cat.updated_date).toLocaleDateString(
                                                          i18n.language === 'fa' ? 'fa-IR' : 'en-US'
                                                      )
                                                    : '-'}
                                            </TableCell>
                                            <TableCell align="center" data-label={t('priority')}>
                                                {cat.priority}
                                            </TableCell>
                                            <TableCell align="center" className="action_buttons">
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => confirmDelete(cat.id)}
                                                    sx={{ marginLeft: '10px' }}
                                                >
                                                    {t('delete')}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="success"
                                                    onClick={() => handleEdit(cat)}
                                                >
                                                    {t('edit')}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <Dialog open={openDialog} onClose={handleCloseDelete}>
                        <DialogTitle>{t('confirm_delete')}</DialogTitle>
                        <DialogContent>
                            <DialogContentText> {t('confirm_delete_category')} </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDelete} color="primary">
                                {t('no')}
                            </Button>
                            <Button onClick={handleConfirmDelete} color="error" autoFocus>
                                {t('yes_delete')}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openEditDialog}
                        onClose={() => setOpenEditDialog(false)}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>{t('edit_category')}</DialogTitle>
                        <DialogContent>
                            {editingCategory && (
                                <>
                                    <TextField
                                        margin="dense"
                                        label={t('category_name')}
                                        name="title"
                                        value={editingCategory.title || ''}
                                        onChange={handleEditChange}
                                        fullWidth
                                        variant="outlined"
                                    />
                                    <TextField
                                        margin="dense"
                                        label={t('priority')}
                                        name="priority"
                                        type="number"
                                        value={
                                            editingCategory.priority !== undefined
                                                ? editingCategory.priority
                                                : ''
                                        }
                                        onChange={handleEditChange}
                                        fullWidth
                                        variant="outlined"
                                    />
                                    <div style={{ marginTop: 16 }}>
                                        <p style={{ margin: '6px 0', fontWeight: 500 }}>
                                            {t('select_icon')}
                                        </p>

                                        <div className="icon-grid">
                                            {DynamicLoader.map((icon) => {
                                                const isActive = editingIcon === icon.key;
                                                return (
                                                    <button
                                                        type="button"
                                                        key={icon.key}
                                                        className={`icon-item ${
                                                            isActive ? 'active' : ''
                                                        }`}
                                                        onClick={() => setEditingIcon(icon.key)}
                                                        aria-pressed={isActive}
                                                    >
                                                        <img src={icon.url} alt={icon.label} />
                                                    </button>
                                                );
                                            })}
                                            {editingIcon && (
                                                <div
                                                    className="icon-preview"
                                                    style={{ marginTop: '10px' }}
                                                >
                                                    <p>{t('selected_icon_preview')}</p>
                                                    <img
                                                        src={
                                                            DynamicLoader.find(
                                                                (icon) => icon.key === editingIcon
                                                            )?.url
                                                        }
                                                        alt={
                                                            DynamicLoader.find(
                                                                (icon) => icon.key === editingIcon
                                                            )?.label
                                                        }
                                                        style={{ width: '60px', height: '60px' }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenEditDialog(false)}>{t('cancel')}</Button>
                            <Button onClick={handleSaveEdit} color="success">
                                {t('save_changes')}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={4000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </div>
            </div>
        </div>
    );
}

export default Categories;
